import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { api } from "../../../singleton/axios";
import { filtersKey } from "../screenerFilters";
import { SCREENER_EXPORT_ROW_CAP } from "../screenerExport";
import { screenerUsageQueryKey } from "./queryKeys";
import type {
  RunRequest,
  ScreenFilters,
  ScreenSort,
  ScreenerApiError,
  ScreenerResultPage,
  ScreenerRunResponse,
  WatchlistFromRunResponse,
} from "../types";
import {
  SCREENER_READ_TIMEOUT_MS,
  screenerRetry,
  screenerRetryDelay,
} from "./screenerRetry";

/** How long rows count as fresh: past it, a return to the tab reads them again. */
export const RESULTS_STALE_MS = 15 * 60 * 1000;

export interface ResultsScope {
  runId: string | null;
  version: number | undefined;
  filters: ScreenFilters | null;
  sort: ScreenSort | undefined;
  columns: readonly string[];
}

const sortKey = (sort: ScreenSort | undefined): string =>
  sort
    ? sort.map((key) => `${key.column}:${key.direction}`).join(",")
    : "default";

// Where the order and the columns sit in resultsQueryKey.
const ORDER_IN_KEY = 5;
const COLUMNS_IN_KEY = 6;
const MATCH_NEUTRAL_PARTS = new Set([ORDER_IN_KEY, COLUMNS_IN_KEY]);

// Two result keys that differ only in their order or columns: the same run,
// version and filters, so the same matches.
const sameMatches = (a: readonly unknown[], b: readonly unknown[]): boolean =>
  a.length === b.length &&
  a.every((part, index) => MATCH_NEUTRAL_PARTS.has(index) || part === b[index]);

export const resultsQueryKey = (scope: ResultsScope) =>
  [
    "screener",
    "rows",
    scope.runId ?? "",
    scope.version ?? 0,
    scope.filters ? filtersKey(scope.filters) : "",
    sortKey(scope.sort),
    [...scope.columns].sort().join(","),
  ] as const;

export class ScreenerRequestError extends Error {
  status: number;
  body: ScreenerApiError | undefined;

  constructor(status: number, body: ScreenerApiError | undefined) {
    super(body?.message ?? body?.error ?? `Request failed (${status})`);
    this.status = status;
    this.body = body;
  }
}

const toRequestError = (error: unknown): ScreenerRequestError => {
  const response = (
    error as { response?: { status?: number; data?: ScreenerApiError } }
  )?.response;
  return new ScreenerRequestError(response?.status ?? 0, response?.data);
};

/**
 * Starts a run session. The first page comes back with the run, so it is
 * written into the results cache for the same scope: the page renders from
 * that page and later pages append through the free rows endpoint.
 */
export function useStartRun() {
  const queryClient = useQueryClient();
  return useMutation<ScreenerRunResponse, ScreenerRequestError, RunRequest>({
    mutationFn: async (body) => {
      try {
        // Bounded like every screener read. Not retried here: the page sends
        // a busy or dropped run again itself, and its Retry button too, with
        // the same idempotency key, so a run the server finished is returned,
        // not charged again.
        const { data } = await api.post<ScreenerRunResponse>(
          "/screener/runs",
          body,
          { timeout: SCREENER_READ_TIMEOUT_MS },
        );
        return data;
      } catch (error) {
        throw toRequestError(error);
      }
    },
    onSuccess: (run, body) => {
      const scope: ResultsScope = {
        runId: run.runId,
        version: run.version,
        filters: body.filters,
        sort: body.sort,
        columns: run.columns,
      };
      queryClient.setQueryData(resultsQueryKey(scope), {
        pages: [run],
        pageParams: [0],
      });
      queryClient.setQueryData(screenerUsageQueryKey, run.allowance);
    },
    // A refused run carries the allowance as the server judged it: the modal
    // and the usage line read that, not an older or failed /usage read.
    onError: (error) => {
      if (error.body?.allowance)
        queryClient.setQueryData(screenerUsageQueryKey, error.body.allowance);
    },
  });
}

/**
 * Rows for an owned run: any filters, sort and page under the session are
 * free. A publication can land between pages (B-2): when page one comes back
 * at a newer version, or a later page is refused with VERSION_CHANGED, the
 * newer version is adopted through `onVersionChanged`, which re-keys the list
 * so it restarts from page one. That is never a new run and never a charge.
 * Page one is seeded under the new key so it is not fetched twice.
 */
export function useScreenResults(
  scope: ResultsScope,
  enabled: boolean,
  onVersionChanged?: (version: number) => void,
) {
  const queryClient = useQueryClient();
  return useInfiniteQuery<ScreenerResultPage, ScreenerRequestError>({
    queryKey: resultsQueryKey(scope),
    queryFn: async ({ pageParam }) => {
      let data: ScreenerResultPage;
      try {
        ({ data } = await api.post<ScreenerResultPage>(
          `/screener/runs/${scope.runId}/rows`,
          {
            filters: scope.filters,
            sort: scope.sort,
            page: pageParam,
            columns: scope.columns.length > 0 ? scope.columns : undefined,
            version: scope.version,
          },
          { timeout: SCREENER_READ_TIMEOUT_MS },
        ));
      } catch (error) {
        const failure = toRequestError(error);
        const moved = failure.body?.version;
        if (
          failure.status === 409 &&
          failure.body?.error === "VERSION_CHANGED" &&
          typeof moved === "number" &&
          moved !== scope.version
        )
          onVersionChanged?.(moved);
        throw failure;
      }
      // Page one fixes the version every later page asks for, including a
      // run resumed before any version was known (no meta).
      if (
        pageParam === 0 &&
        onVersionChanged &&
        data.version !== scope.version
      ) {
        queryClient.setQueryData(
          resultsQueryKey({ ...scope, version: data.version }),
          { pages: [data], pageParams: [0] },
        );
        onVersionChanged(data.version);
      }
      return data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.rows.length < lastPage.pageSize) return undefined;
      if (lastPage.page + 1 >= lastPage.pageCap) return undefined;
      return lastPage.page + 1;
    },
    // A new order or columns view keeps the rows on screen, dimmed, until
    // its page lands: they are the same matches. Any other change withholds
    // them, so old rows never show as matching a different screen
    // (ux-polish-plan §5).
    placeholderData: (previous, previousQuery) =>
      previousQuery &&
      sameMatches(previousQuery.queryKey, resultsQueryKey(scope))
        ? previous
        : undefined,
    enabled: enabled && scope.runId !== null && scope.filters !== null,
    // The data changes once a night: rows stay as long as the page is open.
    // Back on the tab once they're stale, they're read again behind the rows
    // on screen; a new version replaces them (from page one, as above).
    staleTime: RESULTS_STALE_MS,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: screenerRetry,
    retryDelay: screenerRetryDelay,
  });
}

export function useWatchlistFromRun() {
  const queryClient = useQueryClient();
  return useMutation<
    WatchlistFromRunResponse,
    ScreenerRequestError,
    { runId: string; name: string; filters: ScreenFilters; sort?: ScreenSort }
  >({
    mutationFn: async ({ runId, name, filters, sort }) => {
      try {
        const { data } = await api.post<WatchlistFromRunResponse>(
          `/screener/runs/${runId}/watchlist`,
          {
            name,
            filters,
            sort,
          },
        );
        return data;
      } catch (error) {
        throw toRequestError(error);
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["watchlists"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-watchlist"] });
      queryClient.invalidateQueries({ queryKey: ["watchlist-earnings"] });
      enqueueSnackbar(`Added ${result.added} to '${result.watchlist.name}'`, {
        variant: "success",
        autoHideDuration: 3000,
      });
    },
  });
}

/** Web only: the CSV arrives as text and is handed to the browser as a download. */
export function useExportCsv() {
  return useMutation<
    number,
    ScreenerRequestError,
    {
      runId: string;
      filters: ScreenFilters;
      sort?: ScreenSort;
      columns: string[];
      label: string;
      /** The run's match count, for the confirmation. */
      total: number;
    }
  >({
    mutationFn: async ({ runId, filters, sort, columns, label, total }) => {
      let response;
      try {
        response = await api.post<string>(
          `/screener/runs/${runId}/export`,
          { filters, sort, columns, label },
          { responseType: "text", timeout: SCREENER_READ_TIMEOUT_MS },
        );
      } catch (error) {
        throw toRequestError(error);
      }
      const disposition = String(response.headers["content-disposition"] ?? "");
      const filename =
        /filename="([^"]+)"/.exec(disposition)?.[1] ?? "insight-screen.csv";
      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      // The rows the file holds, which can differ from the page's count once
      // a newer nightly is served.
      return Number(
        response.headers["x-export-rows"] ??
          Math.min(total, SCREENER_EXPORT_ROW_CAP),
      );
    },
    onSuccess: (rows, { total }) => {
      if (rows >= SCREENER_EXPORT_ROW_CAP && total > SCREENER_EXPORT_ROW_CAP) {
        enqueueSnackbar(
          `Exported the first ${SCREENER_EXPORT_ROW_CAP.toLocaleString()} of ${total.toLocaleString()} — refine to export the rest`,
          { variant: "info", autoHideDuration: 5000 },
        );
        return;
      }
      enqueueSnackbar(
        `Exported ${rows.toLocaleString()} ${rows === 1 ? "row" : "rows"}`,
        { variant: "success", autoHideDuration: 3000 },
      );
    },
    onError: (error) =>
      enqueueSnackbar(error.body?.message ?? "Could not export the screen", {
        variant: "error",
        autoHideDuration: 5000,
      }),
  });
}
